import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, applyBudgetAdjustment, fetchBudgetAdjustment, type BudgetAdjustmentResponse } from "../services/expensesService";
import { CATEGORY_COLORS, categoryLabel, dateTime, money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Ajuste semanal de presupuestos (#1178): cada lunes a las 6:00 (CDMX) el
// servidor lleva las líneas mensuales del mes en curso a diciembre al ritmo
// real de su categoría. Aquí se ve la última corrida, la propuesta de hoy y se
// puede aplicar al momento. Mismo cálculo que el trabajo programado.

/** Próximo lunes (YYYY-MM-DD) después de `today`. */
export function nextMonday(today: string): string {
  const d = new Date(`${today}T12:00:00Z`);
  const add = ((8 - d.getUTCDay()) % 7) || 7;
  d.setUTCDate(d.getUTCDate() + add);
  return d.toISOString().slice(0, 10);
}

export default function BudgetAutoAdjustCard({ refreshKey, onApplied }: { refreshKey?: unknown; onApplied?: () => void }) {
  const { t, i18n } = useTranslation("common");
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [data, setData] = useState<BudgetAdjustmentResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const ta = (k: string, o?: Record<string, unknown>) => t(`expenses.budgets.auto.${k}`, o);

  const load = useCallback(async () => {
    try {
      setData(await fetchBudgetAdjustment());
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || ta("loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await applyBudgetAdjustment();
      setDone(r.applied);
      await load();
      onApplied?.();
    } catch (err) {
      setError(apiErrorMessage(err) || ta("applyError"));
    } finally {
      setBusy(false);
    }
  };

  const last = data?.last;
  const diff = data ? data.totalAfter - data.totalBefore : 0;

  return (
    <div className={c.card} data-testid="budget-auto-adjust">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
          <i className="ri-calendar-schedule-line" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={`text-sm font-semibold ${c.text}`}>{ta("title")}</h2>
          <p className={`text-xs ${c.muted}`}>{ta("body")}</p>
          <p className={`mt-1 text-xs ${c.muted}`} data-testid="budget-auto-last">
            {last
              ? ta(last.trigger === "semanal" ? "lastWeekly" : "lastManual", { when: dateTime(last.runAt, language), count: last.changes.length })
              : ta("never")}
            {data && ` · ${ta("next", { date: nextMonday(data.today) })}`}
          </p>
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          {done != null && <p className="mt-1 text-xs text-emerald-600">{ta("applied", { count: done })}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={c.secondary} onClick={() => setOpen((o) => !o)} disabled={!data} aria-expanded={open}>
            {ta(open ? "hidePreview" : "showPreview", { count: data?.changes.length ?? 0 })}
          </button>
          <button type="button" className={c.primary} onClick={apply} disabled={busy || !data || data.changes.length === 0}>
            <i className={`mr-1 ${busy ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}`} />
            {ta("applyNow")}
          </button>
        </div>
      </div>

      {open && data && (
        <div className="mt-3 space-y-2">
          <p className={`text-xs ${c.muted}`}>
            {ta("summary", { from: monthLabel(data.from, language), before: money(data.totalBefore, "MXN", language), after: money(data.totalAfter, "MXN", language) })}
            {diff !== 0 && <span className={diff > 0 ? "ml-1 text-red-500" : "ml-1 text-emerald-600"}>({diff > 0 ? "+" : "−"}{money(Math.abs(diff), "MXN", language)})</span>}
          </p>
          {data.changes.length === 0 ? (
            <p className={`text-xs ${c.muted}`}>{ta("noChanges")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1">{t("expenses.fields.category")}</th>
                    <th className="py-1">{ta("month")}</th>
                    <th className="py-1 text-right">{ta("before")}</th>
                    <th className="py-1 text-right">{ta("after")}</th>
                    <th className="py-1 pl-3">{ta("basis")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.changes.map((ch) => (
                    <tr key={ch.lineId} className={`border-t ${c.border} ${c.text}`}>
                      <td className="py-1.5">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[ch.category] }} />
                          {categoryLabel(ch.category, language)}
                        </span>
                      </td>
                      <td className="py-1.5">{monthLabel(ch.period, language)}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(ch.before, "MXN", language)}</td>
                      <td className={`py-1.5 text-right font-semibold tabular-nums ${ch.after > ch.before ? "text-red-500" : "text-emerald-600"}`}>{money(ch.after, "MXN", language)}</td>
                      <td className={`py-1.5 pl-3 text-xs ${c.muted}`}>
                        {ta(ch.method === "ultimo-mes" ? "basisLast" : "basisAverage", { months: ch.basis.map((p) => monthLabel(p, language)).join(", "), rate: money(ch.rate, "MXN", language) })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.skipped.length > 0 && (
            <ul className={`text-xs ${c.muted}`}>
              {data.skipped.map((s) => (
                <li key={s.lineId}>
                  · {categoryLabel(s.category, language)} {monthLabel(s.period, language)}: {ta(`skipped.${s.reason}`)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
